import React, {
  CSSProperties,
  FC,
  ForwardedRef,
  forwardRef,
  ReactNode,
  useEffect,
  useRef,
} from 'react'
import classNames from 'classnames'
import styles from '../styles.module.scss'
import { Icon } from 'shared/ui/Icon'
import { XSmall } from 'shared/assets/icons'

export type IBaseInputProps = {
  size?: 'small' | 'medium'
  variant?: 'header'
  LeftIcon?: FC
  leftContent?: ReactNode
  rightContent?: ReactNode
  isReadOnly?: boolean
  isFocusOnMount?: boolean
  disabled?: boolean
  width?: CSSProperties['width']
  error?: ReactNode
  onClear?: () => void
  wrapClassname?: string
  InputComponent: React.ForwardRefExoticComponent<
    any & React.RefAttributes<HTMLInputElement>
  >
}

export const BaseInput = forwardRef(function BaseInputRef(
  {
    size,
    LeftIcon,
    leftContent,
    rightContent,
    isFocusOnMount,
    width,
    error,
    wrapClassname,
    InputComponent,
    variant,
    ...props
  }: IBaseInputProps,
  ref: ForwardedRef<HTMLLabelElement>
) {
  const refInput = useRef<(HTMLInputElement & { clear?: () => void }) | null>(
    null
  )

  useEffect(() => {
    if (refInput.current && isFocusOnMount) {
      refInput.current.focus()
    }
  }, [isFocusOnMount])

  return (
    <label
      className={classNames(
        styles.wrap,
        {
          [styles.disabled]: props.disabled,
          [styles.readonly]: props.isReadOnly,
          [styles.error]: error,
          [styles[`size__${size}`]]: size,
          [styles[`variant__${variant}`]]: variant,
        },
        wrapClassname
      )}
      style={{ width }}
      ref={ref}
    >
      {leftContent}
      {LeftIcon && (
        <Icon color={'--primary-color-gray-1'}>
          <LeftIcon />
        </Icon>
      )}

      <InputComponent ref={refInput} {...props} />

      {rightContent && (
        <div className={styles.wrapRightContent}>{rightContent}</div>
      )}
      {props.onClear && (
        <button
          className={styles.clear}
          onClick={(e) => {
            e.stopPropagation()
            props.onClear?.()
            refInput.current?.focus()
          }}
        >
          <Icon fontSize={24}>
            <XSmall />
          </Icon>
        </button>
      )}
    </label>
  )
})
